import { useState } from "react";
import {
  listWebhooks,
  createWebhook,
  pauseWebhook,
  resumeWebhook,
  listWebhookDeliveries,
} from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type Webhook = {
  id: string;
  url: string;
  events: string[];
  status: string;
  retryCount: number;
  createdAt: string;
};
type Delivery = {
  id: string;
  eventName: string;
  status: string;
  httpStatus: number | null;
  attempts: number;
  createdAt: string;
};

export function AdminWebhooksPage() {
  const { token } = useAuth();
  const [orgId, setOrgId] = useState("");
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState({
    url: "",
    events: "ORG_CREATED,API_KEY_CREATED",
    createdBy: "",
  });

  async function load() {
    if (!orgId) return;
    const res = (await listWebhooks(token, orgId)) as Webhook[];
    setWebhooks(res ?? []);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    await createWebhook(token, {
      orgId,
      url: form.url,
      events: form.events.split(",").map((e) => e.trim()),
      createdBy: form.createdBy,
    });
    await load();
  }

  async function handleDeliveries(id: string) {
    const res = (await listWebhookDeliveries(token, id)) as Delivery[];
    setDeliveries(res ?? []);
    setSelectedId(id);
  }

  async function handlePause(id: string) {
    await pauseWebhook(token, id);
    await load();
  }

  async function handleResume(id: string) {
    await resumeWebhook(token, id);
    await load();
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-white">Webhooks</h1>

        <div className="space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="flex gap-3">
            <input
              className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Organization ID"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            />
            <button
              onClick={load}
              className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
            >
              Load
            </button>
          </div>

          <form onSubmit={handleCreate} className="flex flex-wrap gap-3">
            <input
              className="min-w-52 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="HTTPS endpoint URL"
              value={form.url}
              onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
              required
            />
            <input
              className="min-w-52 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Events (comma-separated)"
              value={form.events}
              onChange={(e) => setForm((p) => ({ ...p, events: e.target.value }))}
            />
            <input
              className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Created by user ID"
              value={form.createdBy}
              onChange={(e) => setForm((p) => ({ ...p, createdBy: e.target.value }))}
              required
            />
            <button
              type="submit"
              className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
            >
              Create
            </button>
          </form>
        </div>

        {webhooks.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <table className="w-full text-sm text-gray-300">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="pb-2">URL</th>
                  <th className="pb-2">Events</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Retries</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((w) => (
                  <tr key={w.id} className="border-b border-gray-800 last:border-0">
                    <td className="max-w-xs truncate py-2 text-xs text-indigo-400">{w.url}</td>
                    <td className="py-2 text-xs">
                      {w.events.slice(0, 2).join(", ")}
                      {w.events.length > 2 ? `+${w.events.length - 2}` : ""}
                    </td>
                    <td className="py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${w.status === "ACTIVE" ? "bg-green-900 text-green-200" : "bg-yellow-900 text-yellow-200"}`}
                      >
                        {w.status}
                      </span>
                    </td>
                    <td className="py-2 text-gray-500">{w.retryCount}</td>
                    <td className="flex gap-3 py-2">
                      <button
                        onClick={() => handleDeliveries(w.id)}
                        className="text-xs text-indigo-400 hover:underline"
                      >
                        History
                      </button>
                      {w.status === "ACTIVE" ? (
                        <button
                          onClick={() => handlePause(w.id)}
                          className="text-xs text-yellow-400 hover:underline"
                        >
                          Pause
                        </button>
                      ) : (
                        <button
                          onClick={() => handleResume(w.id)}
                          className="text-xs text-green-400 hover:underline"
                        >
                          Resume
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedId && deliveries.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <h2 className="mb-3 text-lg font-semibold text-white">Delivery History</h2>
            {deliveries.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between border-b border-gray-800 py-2 text-sm last:border-0"
              >
                <span className="text-gray-300">{d.eventName}</span>
                <span
                  className={`rounded px-2 py-0.5 text-xs ${d.status === "SUCCESS" ? "bg-green-900 text-green-200" : "bg-red-900 text-red-200"}`}
                >
                  {d.status} {d.httpStatus ? `(${d.httpStatus})` : ""}
                </span>
                <span className="text-xs text-gray-500">Attempts: {d.attempts}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
